/**
 * usePersistentState Hook
 * React Hook，整合 storageUtils，自動同步狀態到 localStorage
 */

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { z } from 'zod';
import { getWithExpiry, setWithExpiry } from '../utils/storage/storageUtils';

/**
 * usePersistentState 選項
 */
interface UsePersistentStateOptions {
  /**
   * 資料範圍：'global' 表示全域，'tenant' 表示租戶隔離（預設）
   */
  scope?: 'global' | 'tenant';
  /**
   * 過期時間（毫秒）
   */
  expiresIn?: number;
  /**
   * 是否在初始化時從 localStorage 讀取（預設 true）
   */
  readOnInit?: boolean;
}

/**
 * 持久化狀態 Hook
 * 自動將狀態同步到 localStorage，並在初始化時從 localStorage 讀取
 *
 * @param key - Storage Key（不含前綴）
 * @param defaultValue - 預設值
 * @param schema - Zod Schema 用於驗證資料結構
 * @param options - Hook 選項
 * @returns [state, setState] 與 useState 相同的 API
 *
 * @example
 * ```typescript
 * const [visibleColumns, setVisibleColumns] = usePersistentState(
 *   'table_cols_admin_products',
 *   new Set(['name', 'price']),
 *   z.array(z.string()).transform(arr => new Set(arr))
 * );
 * ```
 */
export function usePersistentState<T>(
  key: string,
  defaultValue: T,
  schema: z.ZodSchema<T>,
  options: UsePersistentStateOptions = {}
): [T, Dispatch<SetStateAction<T>>] {
  const { scope = 'tenant', expiresIn, readOnInit = true } = options;

  // 初始化狀態：從 localStorage 讀取或使用預設值
  const [state, setState] = useState<T>(() => {
    if (!readOnInit || typeof window === 'undefined') {
      return defaultValue;
    }

    try {
      return getWithExpiry<T>(key, defaultValue, {
        scope,
        schema,
      });
    } catch (error) {
      console.error(`[usePersistentState] 初始化讀取失敗 key "${key}":`, error);
      return defaultValue;
    }
  });

  // 使用 ref 追蹤是否為初始化階段（避免初始化時寫入）
  const isInitialMount = useRef(true);
  const previousKeyRef = useRef(key);
  const previousScopeRef = useRef(scope);

  // 當 key 或 scope 改變時，重新讀取資料
  useEffect(() => {
    if (previousKeyRef.current !== key || previousScopeRef.current !== scope) {
      previousKeyRef.current = key;
      previousScopeRef.current = scope;

      if (typeof window !== 'undefined') {
        try {
          const saved = getWithExpiry<T>(key, defaultValue, {
            scope,
            schema,
          });
          setState(saved);
        } catch (error) {
          console.error(
            `[usePersistentState] Key 改變時讀取失敗 key "${key}":`,
            error
          );
        }
      }
    }
  }, [key, scope, defaultValue, schema]);

  // 當狀態改變時，寫入 localStorage
  useEffect(() => {
    // 跳過初始化階段（避免在初始化時寫入）
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    try {
      setWithExpiry(key, state, {
        scope,
        expiresIn,
        schema,
      });
    } catch (error) {
      console.error(`[usePersistentState] 寫入失敗 key "${key}":`, error);
    }
  }, [state, key, scope, expiresIn, schema]);

  // 優化 setter：使用 useCallback 避免不必要的重新渲染
  const setPersistentState = useCallback<Dispatch<SetStateAction<T>>>(
    (value: SetStateAction<T>) => {
      setState(value);
    },
    []
  );

  return [state, setPersistentState];
}
